import { z } from 'zod';
import { UuidString } from '../../primitives.js';
import { StateGroup } from './state.js';
import { TaskPriority } from './task.js';

/**
 * chat-interactive-actions B1 (SOLUTION §2.4) — shared-подъём массовой операции.
 *
 * До пака `BulkInput.op` жил локальной константой в `api/task-module-tasks-actions.ts`,
 * а фронт держал рукописный union `BulkOp` в `lib/tasks/api/tasks.ts`. Два описания
 * одного контракта расходятся на первой же новой операции молча: сервер отвергает
 * тело, которое фронт считает валидным, и наоборот. С приходом чат-агента
 * появляется ТРЕТИЙ потребитель (инструмент `propose_task_bulk` строит превью по той
 * же операции), поэтому описание одно и живёт здесь.
 *
 * Состав — 14 операций 1:1 с прежней ручкой; семантика каждой описана в
 * `task-module-tasks-actions.ts` рядом с веткой исполнения.
 */

/**
 * Потолок пачки — решение владельца В-2 (SOLUTION §10): 200 → 500. Единственное
 * место, где число задано; обе bulk-ручки (`/tasks/bulk`, `/tasks/bulk-comment`) и
 * превью чата читают его отсюда. Нагрузочный замер пачки 500 — B17.
 */
export const TASK_BULK_MAX_IDS = 500;

/**
 * Каждая операция — INPUT-схема тела запроса: лишнее поле должно ловиться на
 * границе (422), а не молча исполняться как no-op. Поэтому `.strict()`, и поэтому
 * он собран одним хелпером: инвариант `zod-strict` требует объяснения на каждый
 * opt-out, а четырнадцать одинаковых комментариев объясняли бы одно и то же.
 */
// zod-strict-ok: input-схемы тела bulk-запроса — typo в операции обязана падать 422
const op = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict();

export const TaskBulkOp = z.discriminatedUnion('type', [
  op({ type: z.literal('set_state'), state_id: UuidString }),
  // BB13 (M2M) — кросс-проектная выборка: статус по ГРУППЕ; BE ремапит на
  // default-статус группы в primary-workflow каждой задачи.
  op({ type: z.literal('set_state_group'), state_group: StateGroup }),
  op({ type: z.literal('set_priority'), priority: TaskPriority }),
  op({ type: z.literal('set_due'), due_date: z.string().date().nullable() }),
  op({
    type: z.literal('set_dates'),
    start_delta_days: z.number().int().min(-365).max(365),
  }),
  // BE-8 — АБСОЛЮТНЫЕ даты (относительный сдвиг для бездатной задачи — no-op).
  op({
    type: z.literal('set_dates_absolute'),
    start_date: z.string().date().nullable().optional(),
    due_date: z.string().date().nullable().optional(),
  }),
  op({ type: z.literal('set_assignee'), user_id: UuidString.nullable() }),
  // BE-9 — метки, состав ответственных (добавить/снять), постановщик.
  op({ type: z.literal('add_label'), label_id: UuidString }),
  op({ type: z.literal('remove_label'), label_id: UuidString }),
  op({ type: z.literal('add_assignee'), user_id: UuidString }),
  op({ type: z.literal('remove_assignee'), user_id: UuidString }),
  op({ type: z.literal('set_author'), user_id: UuidString }),
  op({ type: z.literal('archive') }),
  op({ type: z.literal('unarchive') }),
]);
export type TaskBulkOp = z.infer<typeof TaskBulkOp>;

/** Имена операций — для лейблов карточки подтверждения (i18n §6.4) и схем инструментов. */
export const TaskBulkOpType = z.enum([
  'set_state',
  'set_state_group',
  'set_priority',
  'set_due',
  'set_dates',
  'set_dates_absolute',
  'set_assignee',
  'add_label',
  'remove_label',
  'add_assignee',
  'remove_assignee',
  'set_author',
  'archive',
  'unarchive',
]);
export type TaskBulkOpType = z.infer<typeof TaskBulkOpType>;

/**
 * Тело `POST /api/v1/task-module/tasks/bulk`.
 *
 * `draft_id` — B16: черновик чата, который это исполнение «потребляет»
 * (condition-UPDATE proposed→consumed, повтор → 409 `draft_already_consumed`).
 * Ручной bulk из BulkEditBar поле не шлёт — и провенанс «из чата» ему не
 * приписывается (I7: происхождение выводится из записи, не из клейма клиента).
 */
export const TaskBulkInput = z
  .object({
    task_ids: z.array(UuidString).min(1).max(TASK_BULK_MAX_IDS),
    op: TaskBulkOp,
    draft_id: UuidString.optional(),
    /**
     * B45 пака `tails-closeout` (WTU-FND-D4) — размер ВСЕГО выбора, который фронт режет
     * на пачки по `TASK_BULK_MAX_IDS`. Операция «по критерию» на сервере сознательно не
     * заводится: состав всегда явный (`ids_only` поиска, B43), сервер видит пачку.
     * Свыше одной пачки выбор — это уже Tier-2 решение: без `confirm_bulk: true` — 422.
     */
    selection_total: z.number().int().positive().max(20_000).optional(),
    confirm_bulk: z.boolean().optional(),
  })
  // zod-strict-ok: dual-use input — валидируется zValidator на ручке и FE перед отправкой
  .strict();
export type TaskBulkInput = z.infer<typeof TaskBulkInput>;

/**
 * Причины, по которым задача выпала из массовой операции ПО БИЗНЕС-ПРАВИЛУ
 * (в отличие от `denied_ids`, где причина — права). Коды структурные: FE
 * локализует через `@staiter/i18n` (словарь §6.4, F10), сервер текста не шлёт.
 */
export const TaskBulkSkipReason = z.enum([
  'state_not_in_project',
  'folder_dates_forbidden',
  'no_dates_to_shift',
  'label_not_in_entity',
  // D1.1 — в workflow проекта нет ни одного статуса запрошенной группы.
  'state_group_missing',
  // D1.2 — исполнитель не входит в пул назначаемых компании задачи.
  'assignee_not_in_entity',
  // D1.2b — то же для постановщика.
  'author_not_in_entity',
]);
export type TaskBulkSkipReason = z.infer<typeof TaskBulkSkipReason>;

/**
 * Ответ обеих bulk-ручек — 1:1 (I2: чат исполняет тем же кодом и читает тот же
 * ответ). `assignee_directory` — денорм-бандл `set_assignee` (BE-1), поле
 * необязательное и здесь не типизируется глубже: его читает только список задач.
 */
export const TaskBulkResult = z.object({
  affected: z.number().int().nonnegative(),
  denied_ids: z.array(UuidString),
  skipped_ids: z.array(z.object({ id: UuidString, reason: TaskBulkSkipReason })),
  /**
   * F19/B27 — строки состава предложения, которые человек снял руками перед
   * применением. Поле есть в ОТВЕТЕ, а не только на черновике: карточка строит
   * отчёт из ответа мутации (кэш к этому моменту может не успеть обновиться), и
   * без него «применено 2 из 3» читалось бы как отказ прав — то есть как ошибка
   * там, где было решение.
   */
  excluded_ids: z.array(UuidString).optional(),
  assignee_directory: z.unknown().optional(),
});
export type TaskBulkResult = z.infer<typeof TaskBulkResult>;

/**
 * Тело `POST /api/v1/task-module/tasks/bulk-comment` (B14, SOLUTION §2.6).
 * Текст plain — TipTap-документ собирает сервер; mentions/вложения не принимаются
 * (не-цель §9.5).
 */
export const TaskBulkCommentInput = z
  .object({
    task_ids: z.array(UuidString).min(1).max(TASK_BULK_MAX_IDS),
    text: z.string().min(1).max(10_000),
    draft_id: UuidString.optional(),
  })
  // zod-strict-ok: dual-use input — zValidator на ручке + FE перед отправкой
  .strict();
export type TaskBulkCommentInput = z.infer<typeof TaskBulkCommentInput>;
